/**
 * UNIT ECONOMICS - Comprehensive per-unit metrics
 */

import React, { useState, useEffect } from 'react';
import { Calendar, RefreshCw, AlertCircle, TrendingUp, DollarSign, Zap, MessageSquare, Target } from 'lucide-react';
import MetricCard from '../components/admin/financials/MetricCard';
import CostChart from '../components/admin/financials/CostChart';
import FinancialNav from '../components/admin/financials/FinancialNav';
import TenantSelector from '../components/admin/financials/TenantSelector';
import apiClient from '../lib/apiClient';

export default function UnitEconomics() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [dateRange, setDateRange] = useState('30d');
  const [tenantFilter, setTenantFilter] = useState('all');

  useEffect(() => {
    loadMetrics();
  }, [dateRange, tenantFilter]);

  const getDateRangeValues = (range) => {
    const today = new Date();
    const to = today.toLocaleDateString('en-CA');
    let from;

    switch (range) {
      case '7d': {
        const d = new Date(today); d.setDate(d.getDate() - 7);
        from = d.toLocaleDateString('en-CA');
        break;
      }
      case '30d': {
        const d = new Date(today); d.setDate(d.getDate() - 30);
        from = d.toLocaleDateString('en-CA');
        break;
      }
      case '90d': {
        const d = new Date(today); d.setDate(d.getDate() - 90);
        from = d.toLocaleDateString('en-CA');
        break;
      }
      case 'ytd':
        from = new Date(today.getFullYear(), 0, 1).toLocaleDateString('en-CA');
        break;
      default: {
        const d = new Date(today); d.setDate(d.getDate() - 30);
        from = d.toLocaleDateString('en-CA');
      }
    }

    return { from, to };
  };

  const loadMetrics = async () => {
    setLoading(true);
    setError(null);
    try {
      const dates = getDateRangeValues(dateRange);
      const params = new URLSearchParams({
        date_from: dates.from,
        date_to: dates.to
      });

      if (tenantFilter !== 'all') {
        params.append('tenant_id', tenantFilter);
      }

      const response = await apiClient.get(`/admin/financials/unit-economics?${params}`);

      if (response.data?.error) {
        throw new Error(response.data.error);
      }

      setMetrics(response.data);
    } catch (err) {
      console.error('Error loading metrics:', err);
      setError(err.message || 'Failed to load unit economics');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              🎯 Unit Economics
            </h1>
            <p className="text-gray-600 mt-2">Platform-wide per-unit cost metrics and efficiency analysis</p>
          </div>
          <button
            onClick={loadMetrics}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="flex gap-4 items-center p-4 bg-white rounded-lg shadow-sm">
          <Calendar className="w-5 h-5 text-gray-400" />
          <div className="flex gap-2">
            <label className="text-sm font-medium text-gray-700">Time Range:</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="text-sm border border-gray-300 rounded px-3 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
              <option value="ytd">Year to Date</option>
            </select>
          </div>
          <div className="w-px h-6 bg-gray-300"></div>
          <TenantSelector value={tenantFilter} onChange={setTenantFilter} />
        </div>
      </div>

      {/* Navigation */}
      <FinancialNav />

      {/* Error State */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-red-900">Failed to load unit economics</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
            <button
              onClick={loadMetrics}
              className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium"
            >
              Try again →
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && !metrics ? (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600 font-medium">Loading unit economics...</p>
          <p className="text-sm text-gray-500 mt-1">Calculating efficiency metrics</p>
        </div>
      ) : (
        <div className={loading ? 'opacity-50 pointer-events-none' : ''}>
          {/* Cost Metrics */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-blue-600" />
              Cost per Unit
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="border-2 border-blue-100 bg-blue-50 rounded-lg p-6 hover:border-blue-300 transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-blue-700">💬 Cost per Message</p>
                  <MessageSquare className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  ${parseFloat(metrics?.cost_per_message || 0).toFixed(4)}
                </p>
              </div>
              <div className="border-2 border-green-100 bg-green-50 rounded-lg p-6 hover:border-green-300 transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-green-700">💬 Cost per Conversation</p>
                  <Target className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  ${parseFloat(metrics?.cost_per_conversation || 0).toFixed(2)}
                </p>
              </div>
              <div className="border-2 border-purple-100 bg-purple-50 rounded-lg p-6 hover:border-purple-300 transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-purple-700">🎯 Cost per Qualified Lead</p>
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  ${parseFloat(metrics?.cost_per_qualified_lead || 0).toFixed(2)}
                </p>
              </div>
              <div className="border-2 border-orange-100 bg-orange-50 rounded-lg p-6 hover:border-orange-300 transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-orange-700">🏆 Cost per Customer Won</p>
                  <Zap className="w-5 h-5 text-orange-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  ${parseFloat(metrics?.cost_per_customer || 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          {/* Efficiency Metrics */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-600" />
              Efficiency Metrics (Higher is Better)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <MetricCard
                label="⚡ Messages per Dollar"
                value={metrics?.messages_per_dollar || 0}
                format="number"
                isLoading={loading}
                icon={<MessageSquare className="w-6 h-6 text-blue-600" />}
              />
              <MetricCard
                label="💬 Conversations per Dollar"
                value={metrics?.conversations_per_dollar || 0}
                format="number"
                isLoading={loading}
                icon={<Target className="w-6 h-6 text-green-600" />}
              />
              <MetricCard
                label="🎯 Leads per Dollar"
                value={metrics?.leads_per_dollar || 0}
                format="number"
                isLoading={loading}
                icon={<TrendingUp className="w-6 h-6 text-purple-600" />}
              />
            </div>
          </div>

          {/* Volume Metrics */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Volume Metrics
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <MetricCard
                label="📊 Total Messages"
                value={metrics?.total_messages || 0}
                format="number"
                isLoading={loading}
              />
              <MetricCard
                label="💬 Total Conversations"
                value={metrics?.total_conversations || 0}
                format="number"
                isLoading={loading}
              />
              <MetricCard
                label="🎯 Qualified Leads"
                value={metrics?.total_qualified_leads || 0}
                format="number"
                isLoading={loading}
              />
              <MetricCard
                label="🏆 Customers Won"
                value={metrics?.total_customers || 0}
                format="number"
                isLoading={loading}
              />
            </div>
          </div>

          {/* Conversion Rates */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-purple-600" />
              Conversion Rates
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <MetricCard
                label="📈 Conversation → Lead Rate"
                value={metrics?.conversation_to_lead_rate || 0}
                format="percent"
                isLoading={loading}
                icon={<TrendingUp className="w-6 h-6 text-green-600" />}
              />
              <MetricCard
                label="🎯 Lead → Customer Rate"
                value={metrics?.lead_to_customer_rate || 0}
                format="percent"
                isLoading={loading}
                icon={<Target className="w-6 h-6 text-purple-600" />}
              />
            </div>
          </div>

          {/* Cost Breakdown Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">🏢 Cost by Provider</h3>
              {metrics && Object.keys(metrics?.cost_breakdown?.by_provider || {}).length > 0 ? (
                <CostChart
                  type="bar"
                  data={Object.entries(metrics?.cost_breakdown?.by_provider || {}).map(([name, value]) => ({
                    date: name.charAt(0).toUpperCase() + name.slice(1),
                    cost: value
                  }))}
                  config={{ xKey: 'date', yKey: 'cost', height: 300 }}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                  <DollarSign className="w-12 h-12 mb-3 text-gray-300" />
                  <p className="font-medium">No provider data</p>
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">📱 Cost by Event Type</h3>
              {metrics && Object.keys(metrics?.cost_breakdown?.by_event_type || {}).length > 0 ? (
                <CostChart
                  type="bar"
                  data={Object.entries(metrics?.cost_breakdown?.by_event_type || {}).map(([name, value]) => ({
                    date: name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                    cost: value
                  }))}
                  config={{ xKey: 'date', yKey: 'cost', height: 300 }}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                  <Zap className="w-12 h-12 mb-3 text-gray-300" />
                  <p className="font-medium">No event data</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
